import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStudio } from "@/context/StudioContext";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  LayoutDashboard, Tv, Compass, Lightbulb, Workflow, Clapperboard,
  CheckCircle2, CalendarDays, BarChart3, Users, Plug, Settings as Cog,
  Search, Bell, Plus, LogOut, ChevronDown, Menu, X, Cpu, Clapperboard as ClapIcon, Wand2, Rocket, Server, Palette, DollarSign, Factory, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CreateProjectDialog } from "@/components/CreateProjectDialog";

const NAV = [
  { section: "section.foundation", items: [
    { to: "/", key: "nav.overview", icon: LayoutDashboard, testid: "overview" },
    { to: "/channels", key: "nav.channels", icon: Tv, testid: "channels" },
    { to: "/command-center", key: "nav.command", icon: Compass, testid: "command" },
  ]},
  { section: "section.production", items: [
    { to: "/research", key: "nav.research", icon: Lightbulb, testid: "research" },
    { to: "/pipeline", key: "nav.pipeline", icon: Workflow, testid: "pipeline" },
    { to: "/autopilot", key: "nav.autopilot", icon: Rocket, testid: "autopilot" },
    { to: "/factory", key: "nav.factory", label: "Channel Factory", icon: Factory, testid: "factory" },
    { to: "/growth", key: "nav.growth", label: "Growth", icon: TrendingUp, testid: "growth" },
    { to: "/styles", key: "nav.styles", label: "Styles", icon: Palette, testid: "styles" },
    { to: "/studio", key: "nav.studio", icon: Clapperboard, testid: "studio" },
    { to: "/review", key: "nav.review", icon: CheckCircle2, testid: "review" },
    { to: "/calendar", key: "nav.calendar", icon: CalendarDays, testid: "calendar" },
    { to: "/analytics", key: "nav.analytics", icon: BarChart3, testid: "analytics" },
    { to: "/earnings", key: "nav.earnings", label: "Earnings", icon: DollarSign, testid: "earnings" },
  ]},
  { section: "section.workspace", items: [
    { to: "/ai-team", key: "nav.team", icon: Users, testid: "ai-team" },
    { to: "/registry", key: "nav.registry", label: "Model & Tool Registry", icon: Cpu, testid: "registry" },
    { to: "/mcp", key: "nav.mcp", label: "MCP Connections", icon: Server, testid: "mcp" },
    { to: "/director", key: "nav.director", label: "Director Settings", icon: Wand2, testid: "director" },
    { to: "/integrations", key: "nav.integrations", icon: Plug, testid: "integrations" },
    { to: "/settings", key: "nav.settings", icon: Cog, testid: "settings" },
  ]},
];

function SearchBox() {
  const { t } = useStudio();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);

  const run = async (value) => {
    setQ(value);
    if (!value.trim()) { setResults(null); setOpen(false); return; }
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(value)}`);
      setResults(data.channels);
      setOpen(true);
    } catch { setResults([]); }
  };

  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
      <Input
        value={q} onChange={(e) => run(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t("top.search")} data-testid="global-search-input"
        className="pl-9 bg-slate-900/80 border-slate-700/80 text-slate-200 placeholder:text-slate-500 h-9 focus-visible:ring-violet-500/30"
      />
      {open && (
        <div className="absolute mt-2 w-full bg-[#111827] border border-slate-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50" data-testid="search-results">
          {results && results.length > 0 ? results.map((r) => (
            <button key={r.id} onMouseDown={() => { navigate(`/channels/${r.id}`); setOpen(false); setQ(""); }}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-800/70 transition-colors border-b border-slate-800/60 last:border-0"
              data-testid={`search-result-${r.id}`}>
              <div className="text-sm text-slate-200">{r.name}{r.archived && <span className="ml-2 text-xs text-slate-500">(archived)</span>}</div>
              <div className="text-xs text-slate-500">{r.niche}</div>
            </button>
          )) : (
            <div className="px-4 py-3 text-sm text-slate-500">No channels found.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Sidebar({ mobileOpen, onClose }) {
  const { t } = useStudio();
  const loc = useLocation();
  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#0B0F17]/95 backdrop-blur-md border-r border-slate-800/80 flex flex-col transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800/80">
          <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center flex-none">
            <Clapperboard className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
          </div>
          <span className="font-display font-extrabold text-slate-50 text-[15px] tracking-tight">Studio OS</span>
          <button className="ml-auto lg:hidden text-slate-400" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {NAV.map((group) => (
            <div key={group.section}>
              <p className="px-3 mb-2 text-[10px] font-semibold tracking-widest uppercase text-slate-600">{t(group.section)}</p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.to);
                  const Icon = item.icon;
                  return (
                    <Link key={item.to} to={item.to} onClick={onClose} data-testid={`nav-item-${item.testid}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-violet-600/15 text-violet-200 font-medium" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"}`}>
                      <Icon className="w-4 h-4 flex-none" /> {item.label || t(item.key)}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

function NotificationBell({ t, navigate }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = async () => { try { const { data } = await api.get("/notifications"); setData(data); } catch (e) { logger.error("notifications load failed", e); } };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);
  const markAll = async (e) => { e?.preventDefault?.(); try { await api.post("/notifications/read-all"); load(); } catch (err) { logger.error("mark-all failed", err); } };
  const open = async (n) => { if (n.link) navigate(n.link); try { await api.post(`/notifications/${n.id}/read`); load(); } catch (err) { logger.error("notification read failed", err); } };
  const dot = (lvl) => lvl === "error" ? "bg-rose-400" : lvl === "success" ? "bg-emerald-400" : "bg-violet-400";

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) load(); }}>
      <DropdownMenuTrigger asChild>
        <button className="relative w-9 h-9 rounded-lg hover:bg-slate-800/60 flex items-center justify-center text-slate-400" data-testid="notifications-button">
          <Bell className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">{data.unread > 9 ? "9+" : data.unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-[#111827] border-slate-700 text-slate-200">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t("top.notifications")}</span>
          {data.unread > 0 && <button onClick={markAll} className="text-xs text-violet-300 hover:underline" data-testid="notifications-read-all">Mark all read</button>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-800" />
        {data.items.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-slate-500" data-testid="notifications-empty">You're all caught up.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
            {data.items.map((n) => (
              <button key={n.id} onClick={() => open(n)} className={`w-full text-left px-3 py-2.5 hover:bg-slate-800/60 border-b border-slate-800/60 last:border-0 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-none ${dot(n.level)}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-slate-600 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, channels, selectedChannelId, setSelectedChannelId } = useStudio();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-[#0B0F17]/90 backdrop-blur-xl border-b border-slate-800/80 sticky top-0 z-40 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></button>

          <Select value={selectedChannelId || "workspace"} onValueChange={(v) => setSelectedChannelId(v === "workspace" ? null : v)}>
            <SelectTrigger className="w-[210px] h-9 bg-slate-900/80 border-slate-700/80 text-slate-200 text-sm" data-testid="global-channel-selector">
              <SelectValue placeholder="Workspace / All channels" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="workspace" data-testid="channel-option-workspace">Workspace / All channels</SelectItem>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`channel-option-${c.id}`}>
                  {c.data.name} <span className="text-slate-500">· {c.data.primary_language}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="hidden sm:block flex-1"><SearchBox /></div>

          <div className="flex items-center gap-2 ml-auto">
            <TooltipProvider>
              <CreateProjectDialog />
            </TooltipProvider>

            <NotificationBell t={t} navigate={navigate} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 h-9 px-2 rounded-lg hover:bg-slate-800/60" data-testid="user-menu-button">
                  <div className="w-7 h-7 rounded-full bg-violet-600/80 flex items-center justify-center text-xs font-bold text-white">
                    {(user?.name || "O")[0].toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#111827] border-slate-700 text-slate-200">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm text-slate-200">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="cursor-pointer focus:bg-slate-800">
                  <Cog className="w-4 h-4 mr-2" /> {t("nav.settings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button" className="cursor-pointer text-rose-400 focus:bg-slate-800 focus:text-rose-300">
                  <LogOut className="w-4 h-4 mr-2" /> {t("top.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px] w-full mx-auto min-w-0 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
